import './../../styles/pages/elections.scss';
import React, { useEffect, useMemo, useState } from 'react';
import {
  getElections,
  createElection,
  updateElection,
  deleteElection
} from '../../core/services/elections';
import { dateFormat } from '../../core/utils/dateFormat';
import {
  MRT_EditActionButtons,
  MaterialReactTable,
  createRow,
  useMaterialReactTable
} from 'material-react-table';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import CsButton from './../../comps/CsButton';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

const Elections = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [limit, setLimit] = useState(10);
  const [lastDocument, setLastDocument] = useState();
  const [globalFilter, setGlobalFilter] = useState();
  const [searchedWithGlobal, setSearchedWithGlobal] = useState(false);

  const loadData = () => {
    setIsLoading(true);

    getElections({ action: 'load', batchLimit: limit }).then(res => {
      setData(res.elections);
      setLastDocument(res.last);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadMoreData = () => {
    setIsLoading(true);

    getElections({
      action: 'more',
      batchLimit: limit,
      start: lastDocument
    }).then(res => {
      setData([...data, ...res.elections]);
      setLastDocument(res.last);
      setIsLoading(false);
    });
  };

  const loadSearchData = () => {
    setIsLoading(true);

    getElections({
      action: 'search',
      batchLimit: null,
      start: null,
      search: globalFilter
    }).then(res => {
      setData(res.elections);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    if (globalFilter !== undefined) {
      loadSearchData();
      setSearchedWithGlobal(true);
    }
  }, [globalFilter]);

  const handleUpdate = async ({ row, values, table }) => {
    table.setEditingRow(null);
    setIsLoading(true);
    await updateElection({
      ...values,
      id: row.original.id
    });
    loadData();
  };

  const handleCreate = async ({ row, values, table }) => {
    table.setCreatingRow(null);
    setIsLoading(true);
    await createElection(values);
    loadData();
  };

  const openDeleteConfirmModal = async row => {
    if (
      window.confirm(`Are you sure you want to delete ${row.original.title}?`)
    ) {
      setIsLoading(true);
      await deleteElection(row.original.id);
      loadData();
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Title',
        size: 50
      },
      {
        accessorKey: 'start_date',
        Cell: ({ cell }) => dateFormat(cell.getValue()),
        header: 'Start Date',
        size: 50,
        muiEditTextFieldProps: {
          type: 'date'
        }
      },
      {
        accessorKey: 'end_date',
        Cell: ({ cell }) => dateFormat(cell.getValue()),
        header: 'End Date',
        size: 50,
        muiEditTextFieldProps: {
          type: 'date'
        }
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 50,
        editVariant: 'select',
        editSelectOptions: [
          {
            label: 'Active',
            value: 'Active'
          },
          {
            label: 'Inactive',
            value: 'Inactive'
          }
        ]
      }
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    createDisplayMode: 'modal',
    enableRowActions: true,
    enableEditing: true,
    manualFiltering: true,
    manualPagination: true,
    enableBottomToolbar: true,
    state: {
      isLoading,
      globalFilter
    },
    muiTableContainerProps: {
      sx: { height: '70vh' }
    },
    onGlobalFilterChange: setGlobalFilter,
    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: 'flex', gap: '10px' }}>
        <CsButton
          icon={faPlus}
          label="New"
          onClick={() => {
            table.setCreatingRow(
              createRow(table, {
                created_at: new Date(),
                updated_at: new Date(),
                status: 'Active'
              })
            );
          }}
        />

        {!data?.length || searchedWithGlobal ? (
          <CsButton
            label="Load Data"
            onClick={() => {
              loadData();
              setSearchedWithGlobal(false);
            }}
          />
        ) : null}

        {data?.length && !searchedWithGlobal ? (
          <CsButton
            label="Load More"
            onClick={() => {
              loadMoreData();
            }}
          />
        ) : null}
      </Box>
    ),
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex' }}>
        <Tooltip title="Edit">
          <IconButton
            onClick={() => {
              table.setEditingRow(row);
            }}
          >
            <Edit />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete">
          <IconButton
            onClick={() => {
              openDeleteConfirmModal(row);
            }}
          >
            <Delete />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    renderEditRowDialogContent: ({ table, row, internalEditComponents }) => (
      <>
        <DialogTitle variant="h6">Edit Election</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
        >
          {internalEditComponents}
        </DialogContent>
        <DialogActions>
          <MRT_EditActionButtons variant="text" table={table} row={row} />
        </DialogActions>
      </>
    ),
    onEditingRowSave: handleUpdate,
    onCreatingRowSave: handleCreate
  });

  return (
    <div className="elections">
      <div className="elections__list">
        <div className="table">
          <MaterialReactTable table={table} />
        </div>
      </div>
    </div>
  );
};

export default Elections;
